import { Typography, Box, Chip } from "@mui/material";
import {
  renderTruncatedText,
  renderKvkkChip,
  renderSubStatusChip,
} from "./DataTable";
import { width } from "styled-system";
import { stripHtmlToText } from "../../../shared/utils/htmlUtils";

// Custom renderer for bullet point descriptions
export const renderBulletPointText = (value: string) => {
  if (!value) return "";

  // Split by bullet points and filter out empty items
  const bulletPoints = value.split("•").filter((item) => item.trim());

  if (bulletPoints.length <= 1) {
    // If no bullet points found, show as regular text
    return (
      <Typography
        variant="body2"
        sx={{
          fontSize: "13px",
          lineHeight: 1.4,
        }}
      >
        {value}
      </Typography>
    );
  }

  return (
    <Box>
      {bulletPoints.map((point, index) => (
        <Typography
          key={index}
          variant="body2"
          sx={{
            fontSize: "13px",
            lineHeight: 1.4,
            marginBottom: index < bulletPoints.length - 1 ? "4px" : 0,
            display: "flex",
            alignItems: "flex-start",
          }}
        >
          <span style={{ marginRight: "6px", fontWeight: "bold" }}>•</span>
          <span style={{ flex: 1 }}>{point.trim()}</span>
        </Typography>
      ))}
    </Box>
  );
};

// Interview result chip renderer
export const renderInterviewResultChip = (value: string) => {
  if (!value) return "";

  const getStatusColor = (status: string) => {
    if (status.toLowerCase().includes("olumlu")) {
      return "#E3FCEF"; // Yeşil
    } else if (
      status.toLowerCase().includes("olumsuz") ||
      status.toLowerCase().includes("red")
    ) {
      return "#FFEBE6"; // Kırmızı
    } else {
      return "#F4F5F7"; // Gri (varsayılan)
    }
  };

  return (
    <Chip
      label={value}
      size="small"
      sx={{
        backgroundColor: getStatusColor(value),
        color: "#42526E",
        fontWeight: 600,
        fontSize: 12,
        borderRadius: "4px",
        border: "none",
      }}
    />
  );
};

// Education columns
export const educationColumns = [
  { key: "collageParameterName", label: "Üniversite" },
  { key: "departmentParameterName", label: "Bölüm" },
  { key: "graduationStatusName", label: "Mezuniyet" },
  { key: "degree", label: "Derece" },
  { key: "registeredYear", label: "Kayıt Yılı" },
  { key: "graduationYear", label: "Mezuniyet Yılı" },
];

// Experience columns
export const experienceColumns = [
  { key: "companyName", label: "Şirket", width: "10%" },
  { key: "positionName", label: "Pozisyon", width: "15%" },
  { key: "startDate", label: "Başlangıç Tarihi", width: "10%" },
  {
    key: "endDate",
    label: "Bitiş Tarihi",
    width: "10%",
    render: (value: string) => value || "Devam ediyor",
  },
  {
    key: "statement",
    label: "Açıklama",
    width: "55%",
    render: renderBulletPointText,
  },
];

// Status columns
export const statusColumns = [
  {
    key: "subStatusName",
    label: "Alt Durum",
    render: renderSubStatusChip,
  },
  { key: "interviewMemberName", label: "Mül. Yapan" },
  {
    key: "interviewDate",
    label: "Mül. Tarihi",
    render: (value: string) => {
      if (!value) return "";
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      const dd = d.getDate().toString().padStart(2, "0");
      const mm = (d.getMonth() + 1).toString().padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = d.getHours().toString().padStart(2, "0");
      const min = d.getMinutes().toString().padStart(2, "0");
      return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    },
  },
  {
    key: "resultParameterName",
    label: "Mül. Sonucu",
    render: renderInterviewResultChip,
  },
  { key: "customerName", label: "Müşteri", width: "100px" },
  { key: "positionName", label: "Pozisyon" },
  {
    key: "createDate",
    label: "Durum Tarihi",
    render: (value: string) => {
      if (!value) return "";
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      const dd = d.getDate().toString().padStart(2, "0");
      const mm = (d.getMonth() + 1).toString().padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = d.getHours().toString().padStart(2, "0");
      const min = d.getMinutes().toString().padStart(2, "0");
      return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    },
  },
  {
    key: "kvkkStatus",
    label: "KVKK Durumu",
    render: renderKvkkChip,
  },
  {
    key: "statusNotes",
    label: "Notlar",
    width: "400px",
    maxWidth: "400px",
    // Notlar artik HTML tutuyor (D20). stripHtmlToText olmadan kartta
    // `<p>Not</p>` gorunur. renderTruncatedText curried: (maxWidth, lines) => (value).
    render: (value: string) =>
      renderTruncatedText("400px", 3)(stripHtmlToText(value)),
  },
];

// Language columns
export const languageColumns = [
  { key: "languageName", label: "Dil" },
  { key: "readingLevel", label: "Okuma Seviyesi" },
  { key: "writingLevel", label: "Yazma Seviyesi" },
  { key: "speakingLevel", label: "Konuşma Seviyesi" },
];

// Salary columns
export const salaryColumns = [
  {
    key: "createdDate",
    label: "Oluşturma Tarihi",
    render: (value: string) => {
      if (!value) return "";
      return new Date(value).toLocaleDateString("tr-TR");
    },
  },
  {
    key: "salaryExpectation",
    label: "Maaş Beklentisi",
    render: (value: number, row: any) => {
      const formatted = value?.toLocaleString("tr-TR");
      const currency = row?.currencyCode || "TL";
      return `${formatted} ${currency}`;
    },
  },
];

// Certificate columns
export const certificateColumns = [
  {
    key: "certificateDate",
    label: "Tarih",
    width: "120px",
    render: (value: string) => {
      if (!value) return "";
      return new Date(value).toLocaleDateString("tr-TR");
    },
  },
  { key: "certificateName", label: "Sertifika / Kurs Adı", width: "250px" },
  { key: "certificateEnterprise", label: "Kurum", width: "200px" },
  {
    key: "statement",
    label: "Açıklama",
    render: renderBulletPointText,
  },
];

// Project / Award columns
export const projectAwardColumns = [
  {
    key: "awardOrProjectDate",
    label: "Tarih",
    width: "120px",
    render: (value: string) => {
      if (!value) return "";
      return new Date(value).toLocaleDateString("tr-TR");
    },
  },
  { key: "awardOrProjectName", label: "Proje / Ödül Adı", width: "250px" },
  {
    key: "awardOrProjectEnterprise",
    label: "Kurum / Organizasyon",
    width: "200px",
  },
  {
    key: "statement",
    label: "Açıklama",
    render: renderBulletPointText,
  },
];
export const emailColumns = [
  { key: "communicationType", label: "Kategori", width: "150px" },
  { key: "emailName", label: "Email Adresi", width: "auto" },
];

export const phoneColumns = [
  { key: "communicationType", label: "Kategori", width: "150px" },
  { key: "phoneNo", label: "Telefon Numarası", width: "auto" },
];

export const addressColumns = [
  { key: "communicationType", label: "Kategori", width: "150px" },
  {
    key: "fullAddress",
    label: "Adres",
    width: "auto",
    render: (value: string, row: any) => {
      return (
        value ||
        `${row.addressLine}${row.townName ? `, ${row.townName}` : ""}${
          row.cityName ? `, ${row.cityName}` : ""
        }${row.countryName ? `, ${row.countryName}` : ""}${
          row.postcode ? `, ${row.postcode}` : ""
        }`
      );
    },
  },
];
